import daLocale from "date-fns/locale/da";
import svLocale from "date-fns/locale/sv";
import nbLocale from "date-fns/locale/nb";
import format from "date-fns/format";
import { useCallback, useState } from "react";
import { useRouter } from "next/router";

import {
  DELIVERY_INTERVALS,
  DeliveryInterval,
  WEEKDAY,
  language,
} from "@chef/constants";
import {
  TimeblocksByRangeWithGeoQuery,
  PreferencesQuery,
  TimeblocksQuery,
} from "@chef/state-management";
import { formatTimeInterval } from "@chef/helpers";
import { isNotEmptyArray } from "@chef/utils/array";
import { getAttribute } from "@chef/state-management/helpers";
import { normalizeDate } from "@chef/utils/time";

export type Timeblock =
  TimeblocksByRangeWithGeoQuery["timeblocksByRangeWithGeo"]["weeks"][number]["timeblocks"][number];

export const getCity = (
  weekAndYearTimeblocks: TimeblocksByRangeWithGeoQuery["timeblocksByRangeWithGeo"]["weeks"],
) => {
  const timeblock = weekAndYearTimeblocks.find((t) =>
    isNotEmptyArray(t.timeblocks),
  );

  if (!timeblock) {
    return null;
  }

  return timeblock.timeblocks[0].city;
};

export const testHasDeliveries = (
  weekAndYearTimeblocks: TimeblocksByRangeWithGeoQuery["timeblocksByRangeWithGeo"]["weeks"],
) => {
  return weekAndYearTimeblocks.some((tb) => {
    if (!tb.timeblocks) {
      return false;
    }
    return tb.timeblocks.some((t) => !t.noDelivery);
  });
};

export const formatDeliveryTimeString = (timeblock: Timeblock) => {
  const { firstDeliveryDate, weekday } = timeblock;

  const dayName = intl.WEEKDAY[weekday as WEEKDAY];
  const formatteDeliveryDate = format(new Date(firstDeliveryDate), "dd.MMM", {
    locale: intl.LOCALE,
  });

  return `${dayName} ${formatteDeliveryDate}`;
};

export const deliveryTimeStringWthVisibleTimeblocks = (
  timeblocks: Timeblock[],
  firstDelivery: string,
  firstDeliveryTimeblockId: number,
) => {
  const selectedTimeblock = timeblocks.find(
    (tb) =>
      tb.timeblockId === +firstDeliveryTimeblockId &&
      normalizeDate(new Date(tb.deliveryDate)) ===
        normalizeDate(new Date(firstDelivery)),
  );

  if (!selectedTimeblock) {
    return "";
  }
  const { from, to, firstDeliveryDate, weekday } = selectedTimeblock;
  const dayName = intl.WEEKDAY[weekday as WEEKDAY];

  const formattedTimeInterval = formatTimeInterval(from, to, "m", " - ");
  return intl.FORMAT_DELIVERY_DATE({
    dayName,
    deliveryDate: new Date(firstDeliveryDate),
    formattedTimeInterval,
  });
};

export const createDeliveryOptionsFromTimeblocks = (
  timeblocks: Timeblock[],
) => {
  const options: {
    label: string;
    value: string;
    timeblocks: Timeblock[];
    registrationTypeId: string;
  }[] = [];

  for (const tb of timeblocks) {
    const { firstDeliveryDate, noDelivery, registrationTypeId } = tb;

    const label = formatDeliveryTimeString(tb);

    !noDelivery &&
      options.push({
        label,
        value: firstDeliveryDate,
        timeblocks: [tb],
        registrationTypeId,
      });
  }

  return options;
};

export const createDeliveryFrequencyLabel = (
  timeblock: Timeblock[],
  firstDelivery: string,
  frequency?: DeliveryInterval,
) => {
  const firstDeliveryTime = firstDelivery.split("_")[0];

  const firstDeliveryDay = format(new Date(firstDeliveryTime), "EEEE", {
    locale: intl.LOCALE,
  });

  let deliveryFrequencyLabel = firstDelivery;

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore https://github.com/microsoft/TypeScript/issues/33591 - find seems broken on union types
  const frequencyText = intl.FREQUENCY_OPTIONS.frequency.find(
    (frequencyOption: { value: DeliveryInterval }) =>
      frequencyOption.value === frequency,
  )?.text;
  if (frequencyText) {
    deliveryFrequencyLabel = `${frequencyText}, ${firstDeliveryDay}`;
  }

  const firstDeliveryTimeblock = timeblock.find(
    (timeblock) => timeblock.firstDeliveryDate === firstDelivery.split("_")[0],
  );
  if (!firstDeliveryTimeblock) {
    return deliveryFrequencyLabel;
  }

  const { from, to } = firstDeliveryTimeblock || {};

  const formattedTimeInterval = formatTimeInterval(from, to, "m", " - ");

  return intl.FORMAT_DELIVERY_FREQUENCY_LABEL({
    deliveryFrequencyLabel,
    formattedTimeInterval,
  });
};

export const createDeliveryTimeslotOptions = (
  timeblocks: TimeblocksQuery["timeblocksData"]["timeblocks"],
) => {
  const mod = (n: number, m: number) => ((n % m) + m) % m;
  return [...timeblocks]
    .sort((a, b) => mod(a.weekday + 1, 7) - mod(b.weekday + 1, 7)) // Sort by weekday, Saturday -> Friday
    .map((timeblock) => {
      const weekDay = intl.WEEKDAY[timeblock.weekday as WEEKDAY];

      return {
        label: `${weekDay}, ${formatTimeInterval(
          timeblock.from,
          timeblock.to,
          "m",
          "-",
        )}`,
        value: timeblock.timeblockId,
      };
    });
};

export const createDeliveryFrequenceLabel = (
  deliveryFrequency: number | undefined,
) => {
  if (!deliveryFrequency) {
    return null;
  }
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore https://github.com/microsoft/TypeScript/issues/33591 - find seems broken on union types
  const frequencyText = intl.FREQUENCY_OPTIONS.frequency.find(
    (frequencyOption: { value: DeliveryInterval }) =>
      frequencyOption.value === deliveryFrequency,
  )?.text;
  return frequencyText;
};

export const filterAvailableWeeklyTimeblocks = (
  weeks: TimeblocksByRangeWithGeoQuery["timeblocksByRangeWithGeo"]["weeks"],
) => {
  const firstAvailableIdx = weeks.findIndex(({ timeblocks }) =>
    timeblocks.some((t) => !(t.noDelivery && t.fallbackTimeblockId === null)),
  );

  return weeks.slice(firstAvailableIdx, firstAvailableIdx + 4);
};

type State = Record<string, string | number | string[]>;

/**
 * Custom hook for managing and syncing state with URL search parameters.
 *
 * This hook allows you to:
 * - Initialize a state object based on URL search parameters.
 * - Keep the state synchronized with the URL.
 * - Update the URL parameters when the state changes.
 *
 * @param initialState - The default state values to populate when no URL parameters are present.
 * @returns An object containing:
 *   - `state`: The current state derived from URL parameters or the initial state.
 *   - `updateParams`: A function to update the URL search parameters and synchronize them with the state.
 *
 *  `updateParams` takes the following parameters:
 *         - `updates`: A record of keys and values to update in the URL parameters.
 *                     Values can be strings, numbers, or arrays of strings.
 *         - `unwantedKeys`: An optional array of keys to exclude from the URL parameters.
 *                          Keys with undefined, null, empty strings, or empty arrays are automatically removed.
 */

export const useSearchParams = <T extends State>(initialState: T) => {
  const router = useRouter();
  const [state] = useState<State>(() => {
    // Initialize state based on URL search parameters or default to the initial state
    if (typeof window === "undefined") {
      return initialState;
    }
    // Parse the current URL search parameters
    const params = new URLSearchParams(window.location.search);
    const newState = { ...initialState };

    // Map URL search parameters to the state structure
    for (const [key, value] of params.entries()) {
      if (value.includes("-")) {
        (newState[key] as string[]) = value.split("-");
      } else if (!isNaN(Number(value))) {
        (newState[key] as number) = Number(value);
      } else {
        (newState[key] as string) = value;
      }
    }
    return newState;
  });

  const updateParams = useCallback(
    (
      updates: Record<string, string | number | string[]>,
      unwantedKeys?: string[],
    ) => {
      const searchParams = new URLSearchParams(window.location.search);
      let hasChanges = false;

      // Process updates and check for differences
      Object.entries({ ...state, ...updates }).forEach(([key, value]) => {
        const newValue = Array.isArray(value)
          ? value.join("-")
          : value?.toString();

        const currentValue = searchParams.get(key);

        if (
          value === undefined ||
          value === null ||
          value === "" ||
          (Array.isArray(value) && value.length === 0) ||
          unwantedKeys?.includes(key)
        ) {
          if (currentValue !== null) {
            // The parameter exists in the URL but needs to be removed
            hasChanges = true;
            searchParams.delete(key);
          }
        } else if (currentValue !== newValue) {
          // The parameter exists but has changed
          hasChanges = true;
          searchParams.set(key, newValue);
        }
      });

      // Handle keys in the current URL that are not in the updates
      for (const key of Array.from(searchParams.keys())) {
        if (!(key in updates) && !(key in state)) {
          hasChanges = true;
          searchParams.delete(key);
        }
      }

      if (hasChanges) {
        router.replace({ query: Object.fromEntries(searchParams) }, undefined, {
          shallow: true,
        });
      }
    },
    [router, state],
  );

  return { state, updateParams };
};

/**
 * Maps an array of preferences based on their IDs to get corresponding slugs.
 *
 * @param ids - The list of preference IDs to map.
 * @param preferences - The available preferences to map from.
 * @returns An array of slugs corresponding to the provided IDs.
 */

export const getSlugsFromIds = (
  ids: string[] | undefined,
  preferences: PreferencesQuery["preferences"],
): string[] => {
  if (!Array.isArray(ids)) {
    return [];
  }
  return preferences
    .filter((preference) => ids.includes(preference.preferenceId))
    .map((preference) => getAttribute("preference_slug", preference) || "");
};

/**
 * Maps an array of preferences based on their slugs to get corresponding IDs.
 *
 * @param slugs - The slugs to map.
 * @param preferences - The available preferences to map from.
 * @returns An array of preference IDs corresponding to the provided slugs.
 */

export const getIdsFromSlugs = (
  slugs: string | string[] | undefined,
  preferences: PreferencesQuery["preferences"],
): string[] => {
  if (!slugs) {
    return [];
  }

  const slugArray = Array.isArray(slugs) ? slugs : [slugs];
  const slugToIdMap = preferences.reduce((acc, pref) => {
    const slug = getAttribute("preference_slug", pref);
    if (slug) {
      acc[slug] = pref.preferenceId;
    }
    return acc;
  }, {} as Record<string, string>);

  return slugArray.map((slug) => slugToIdMap[slug]).filter(Boolean);
};

/**
 * Validates and returns a valid portion value.
 * If the `queryPortion` is not in `portionOptions`, it falls back to `defaultPortion`.
 *
 * @param {string | number | undefined} queryPortion - The portion value from the query params.
 * @param {number[]} portionOptions - Array of valid portion options.
 * @param {number} defaultPortion - The default portion to use if the query portion is invalid.
 * @returns {number} The valid portion value.
 */
export const getValidPortion = (
  queryPortion: string | number | string[],
  portionOptions: number[],
  defaultPortion: number,
): number => {
  const parsedPortion = Number(queryPortion);
  return portionOptions.includes(parsedPortion)
    ? parsedPortion
    : defaultPortion;
};

/**
 * Validates and returns a valid meal value.
 * If the `queryMeal` is not in `mealOptions`, it falls back to `defaultMeal`.
 *
 * @param {string | number | undefined} queryMeal - The meal value from the query params.
 * @param {number[] | undefined} mealOptions - Array of valid meal options.
 * @param {number} defaultMeal - The default meal to use if the query meal is invalid.
 * @returns {number} The valid meal value.
 */
export const getValidMeal = (
  queryMeal: string | number | string[],
  mealOptions: number[] | undefined,
  defaultMeal: number,
): number => {
  const parsedMeal = Number(queryMeal);
  return mealOptions?.includes(parsedMeal) ? parsedMeal : defaultMeal;
};

const intl = (
  {
    no: {
      LENGTH_OF_PERSONAL_NUMBER: 11,
      // LENGTH_OF_ORGANIZATION_NUMBER: 10,
      LENGTH_OF_PHONE_NUMBER: 8,
      LOCALE: nbLocale,
      MOCK_SSN: "18888888886",

      WEEKDAY: {
        0: "Søndag",
        1: "Mandag",
        2: "Tirsdag",
        3: "Onsdag",
        4: "Torsdag",
        5: "Fredag",
        6: "Lørdag",
      },
      FREQUENCY_OPTIONS: {
        frequency: [
          {
            value: DELIVERY_INTERVALS.EVERY_WEEK,
            text: "Hver uke",
            label: "Hver uke",
          },
          {
            value: DELIVERY_INTERVALS.EVEN_WEEKS,
            text: "Partallsuker",
            label: "Partallsuker",
          },
          {
            value: DELIVERY_INTERVALS.ODD_WEEKS,
            text: "Oddetallsuker",
            label: "Oddetallsuker",
          },
        ],
        frequencyTypes: [
          { value: 10, text: "hver uke" },
          { value: 99, text: "annenhver uke" },
        ],
        frequencyBiWeeks: [
          { value: 20, text: "partallsuker" },
          { value: 30, text: "oddetallsuker" },
        ],
      },
      FORMAT_DELIVERY_DATE: ({
        dayName,
        deliveryDate,
        formattedTimeInterval,
      }: {
        dayName: string;
        deliveryDate: Date;
        formattedTimeInterval: string;
      }) => {
        const formattedDeliveryDate = format(
          new Date(deliveryDate),
          "dd. MMMM",
          {
            locale: intl.LOCALE,
          },
        );

        return `${dayName} ${formattedDeliveryDate}, ${formattedTimeInterval}`;
      },

      FORMAT_DELIVERY_FREQUENCY_LABEL: ({
        deliveryFrequencyLabel,
        formattedTimeInterval,
      }: {
        deliveryFrequencyLabel: string;
        formattedTimeInterval: string;
      }) => {
        return `${deliveryFrequencyLabel}, ${formattedTimeInterval}`;
      },
    },

    se: {
      LENGTH_OF_PERSONAL_NUMBER: 12,
      LENGTH_OF_PHONE_NUMBER: 10,
      LOCALE: svLocale,

      WEEKDAY: {
        0: "Söndag",
        1: "Måndag",
        2: "Tisdag",
        3: "Onsdag",
        4: "Torsdag",
        5: "Fredag",
        6: "Lördag",
      },
      FREQUENCY_OPTIONS: {
        frequency: [
          { value: DELIVERY_INTERVALS.EVERY_WEEK, text: "Varje vecka" },
          { value: DELIVERY_INTERVALS.EVEN_WEEKS, text: "Jämna veckor" },
          { value: DELIVERY_INTERVALS.ODD_WEEKS, text: "Udda veckor" },
        ],
        frequencyTypes: [
          { value: 10, text: "hver uke" },
          { value: 99, text: "annenhver uke" },
        ],
        frequencyBiWeeks: [
          { value: 20, text: "partallsuker" },
          { value: 30, text: "oddetallsuker" },
        ],
      },

      FORMAT_DELIVERY_DATE: ({
        dayName,
        deliveryDate,
        formattedTimeInterval,
      }: {
        dayName: string;
        deliveryDate: Date;
        formattedTimeInterval: string;
      }) => {
        const formattedDeliveryDate = format(
          new Date(deliveryDate),
          "dd. MMMM",
          {
            locale: intl.LOCALE,
          },
        );

        return `${dayName} ${formattedDeliveryDate}, ${formattedTimeInterval}`;
      },

      FORMAT_DELIVERY_FREQUENCY_LABEL: ({
        deliveryFrequencyLabel,
        formattedTimeInterval,
      }: {
        deliveryFrequencyLabel: string;
        formattedTimeInterval: string;
      }) => {
        return `${deliveryFrequencyLabel}, ${formattedTimeInterval}`;
      },
    },

    dk: {
      LOCALE: daLocale,
      LENGTH_OF_PHONE_NUMBER: 8,
      SKIP_SSN_VALIDATION: true,

      WEEKDAY: {
        0: "Søndag",
        1: "Mandag",
        2: "Tirsdag",
        3: "Onsdag",
        4: "Torsdag",
        5: "Fredag",
        6: "Lørdag",
      },
      FREQUENCY_OPTIONS: {
        frequency: [
          { value: DELIVERY_INTERVALS.EVERY_WEEK, text: "Hver uge" },
          { value: DELIVERY_INTERVALS.EVEN_WEEKS, text: "Lige ugenumre" },
          { value: DELIVERY_INTERVALS.ODD_WEEKS, text: "Ulige ugenumre" },
        ],
        frequencyTypes: [
          { value: 10, text: "Hver uge" },
          //TODO : Translate in RN
          { value: 99, text: "annenhver uke" },
        ],
        frequencyBiWeeks: [
          { value: 20, text: "Lige ugenumre" },
          { value: 30, text: "Ulige ugenumre" },
        ],
      },

      FORMAT_DELIVERY_DATE: ({
        dayName,
        deliveryDate,
        formattedTimeInterval,
      }: {
        dayName: string;
        deliveryDate: Date;
        formattedTimeInterval: string;
      }) => {
        const formattedDeliveryDate = format(
          new Date(deliveryDate),
          "dd. MMM",
          {
            locale: intl.LOCALE,
          },
        );

        return `${dayName} d. ${formattedDeliveryDate} kl. ${formattedTimeInterval}`;
      },

      FORMAT_DELIVERY_FREQUENCY_LABEL: ({
        deliveryFrequencyLabel,
        formattedTimeInterval,
      }: {
        deliveryFrequencyLabel: string;
        formattedTimeInterval: string;
      }) => {
        return `${deliveryFrequencyLabel}, kl. ${formattedTimeInterval}`;
      },
    },
  } as const
)[language];
